package org.maplibre.compose.expressions.dsl

import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.value.AnyValue
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.FloatValue
import org.maplibre.compose.expressions.value.GeoJsonValue
import org.maplibre.compose.expressions.value.GeometryType
import org.maplibre.compose.expressions.value.MapValue
import org.maplibre.compose.expressions.value.StringValue

/** Object to access feature-related data, see [feature] */
public object Feature {
  /**
   * Returns the value corresponding to the given [key] in the current feature's properties or
   * `null` if it is not present.
   *
   * The type of the value is known only when the map evaluates the expression. See [AnyValue] for
   * which operations accept it directly and how to give it a known type.
   */
  public operator fun get(key: Expression<StringValue>): Expression<AnyValue?> = call("get", key)

  /**
   * Returns the value corresponding to the given [key] in the current feature's properties or
   * `null` if it is not present.
   *
   * The type of the value is known only when the map evaluates the expression. See [AnyValue] for
   * which operations accept it directly and how to give it a known type.
   */
  public operator fun get(key: String): Expression<AnyValue?> = get(const(key))

  /** Tests for the presence of a property value [key] in the current feature's properties. */
  public fun has(key: Expression<StringValue>): Expression<BooleanValue> = call("has", key)

  /** Tests for the presence of a property value [key] in the current feature's properties. */
  public fun has(key: String): Expression<BooleanValue> = has(const(key))

  /**
   * Gets the feature properties object. Note that in some cases, it may be more efficient to use
   * [get]`("property_name")` directly.
   */
  public fun properties(): Expression<MapValue<AnyValue>> = call("properties")

  /**
   * Retrieves a property value from the current feature's state. Returns `null` if the requested
   * property is not present on the feature's state. The type of the value is known only when the
   * map evaluates the expression; see [AnyValue].
   *
   * A feature's state is runtime data for one loaded style. It is separate from GeoJSON and vector
   * tile data.
   *
   * Features are identified by their `id`, which must be an integer or a string that can be cast to
   * an integer. In the browser, a base-style source that sets `promoteId` identifies its features
   * by that property instead; MapLibre Native ignores `promoteId`. Only data-driven paint
   * properties documented as supporting feature state accept [state].
   */
  public fun state(key: Expression<StringValue>): Expression<AnyValue?> = call("feature-state", key)

  /**
   * Retrieves a property value from the current feature's state. Returns `null` if the requested
   * property is not present on the feature's state. The type of the value is known only when the
   * map evaluates the expression; see [AnyValue].
   *
   * A feature's state is runtime data for one loaded style. It is separate from GeoJSON and vector
   * tile data.
   *
   * Features are identified by their `id`, which must be an integer or a string that can be cast to
   * an integer. In the browser, a base-style source that sets `promoteId` identifies its features
   * by that property instead; MapLibre Native ignores `promoteId`. Only data-driven paint
   * properties documented as supporting feature state accept [state].
   */
  public fun state(key: String): Expression<AnyValue?> = state(const(key))

  /** Gets the feature's geometry type. */
  public fun geometryType(): Expression<GeometryType> = call("geometry-type")

  /** Gets the feature's id, if it has one. */
  public fun id(): Expression<AnyValue?> = call("id")

  /**
   * Gets the progress along a gradient line. Can only be used in the `gradient` property of a line
   * layer, see [LineLayer][org.maplibre.compose.layers.LineLayer].
   */
  public fun lineProgress(): Expression<FloatValue> = call("line-progress")

  /**
   * Gets the value of a cluster property accumulated so far. Can only be used in the
   * `clusterProperties` option of a clustered GeoJSON source, see
   * [GeoJsonOptions][org.maplibre.compose.sources.GeoJsonOptions].
   */
  public fun accumulated(): Expression<AnyValue> = call("accumulated")

  /**
   * Returns true if the evaluated feature is fully contained inside a boundary of the input
   * geometry, false otherwise. Supported features for evaluation:
   * - Point: Returns false if a point is on the boundary or falls outside the boundary.
   * - LineString: Returns false if any part of a line falls outside the boundary, the line
   *   intersects the boundary, or a line's endpoint is on the boundary.
   *
   * @param geometry Can be a valid GeoJSON of type Polygon, MultiPolygon, Feature, or
   *   FeatureCollection.
   */
  public fun within(geometry: Expression<GeoJsonValue>): Expression<BooleanValue> =
    call("within", geometry)

  /** Returns the shortest distance in meters between the evaluated feature and [geometry]. */
  public fun distance(geometry: Expression<GeoJsonValue>): Expression<FloatValue> =
    call("distance", geometry)
}

/** Accesses to feature-related data */
public val feature: Feature = Feature
